import { Flex, Heading, Select, Text } from "@applyboard/crystal-ui"
import { useState, ComponentType } from "react"
import { DestinationCountryStatus, DocumentTags } from "../../../../utils"
import { FileUploadField } from "../FileUploadField"
import { ALLOWED_FILE_TYPES } from "../../../../utils/constants"
import { StudentApplication } from "../../types"
import { useApplicationFormContext } from "../ApplicationForm"
import { PersonalInformationFormFields } from "./types"
import { Asterisk } from "../../../Asterisk"

// The Field component from createForm has extremely complex generic types
// that make it impractical to type exactly without 'any'.
// Following TypeScript's guidance, we use a branded type to maintain type safety
// while acknowledging the limitations of typing polymorphic components.
// See: https://www.typescriptlang.org/docs/handbook/2/narrowing.html#discriminated-unions
type FieldComponentType = ComponentType<Record<string, unknown>>

type StatusSectionProps = Readonly<{
  Field: FieldComponentType
  setFieldValues: (values: Partial<PersonalInformationFormFields>) => void
  destinationCountryName?: string
  disabled?: boolean
  isRequestingPassportApi?: boolean
  application: StudentApplication
  immigrationStatus: PersonalInformationFormFields['immigrationStatus']
  immigrationFiles: PersonalInformationFormFields['immigrationFiles']
}>

export function StatusSection({ Field, ...props }: StatusSectionProps) {
  const { addPendingDelete, getObservableFiles } = useApplicationFormContext()
  const [hasValidVisa, setHasValidVisa] = useState(
    props.immigrationStatus !== 'NO_STATUS' && !!props.immigrationStatus,
  )
  

  const immigrationStatusList = [
    {
      key: crypto.randomUUID(),
      value: DestinationCountryStatus.NO_STATUS,
      label: "I don't have this",
    },
    {
      key: crypto.randomUUID(),
      value: DestinationCountryStatus.STUDY_PERMIT,
      label: 'Study permit',
    },
    { key: crypto.randomUUID(), value: DestinationCountryStatus.WORK_PERMIT, label: 'Work permit' },
    {
      key: crypto.randomUUID(),
      value: DestinationCountryStatus.VISITOR,
      label: 'Visitor/Tourist visa',
    },
    {
      key: crypto.randomUUID(),
      value: DestinationCountryStatus.OTHER,
      label: 'Other',
    },
    {
    key: crypto.randomUUID(),
    value: DestinationCountryStatus.REFUGEE,
    label: 'Refugee',
  },
  {
    key: crypto.randomUUID(),
    value: DestinationCountryStatus.REFUGEE_CLAIMANT,
    label: 'Refugee claimant',
  },
  ]

  return (
    <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap pt={4} pb={4}>
      <Flex basis="100%">
        <Heading variant="titleS" level={3}>
          ✈️ Step 3: Provide destination status
        </Heading>
      </Flex>
      <Flex basis="100%" direction="column">
        <Heading variant="titleS" level={3}>
          🌎 {props.destinationCountryName || 'Destination Country'} Status
        </Heading>
        <Text variant="bodyS">
          Tell us below the student's current visa/immigration status in {props.destinationCountryName}.
        </Text>
      </Flex>
      <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
        <Field
          as={Select}
          label="Status"
          name="immigrationStatus"
          appearance="styled"
          disabled={props.disabled || props.isRequestingPassportApi}
          required={!props.disabled ? 'Status is required' : false}
          onChange={(v: string) => {
            const fileIds = Object.keys(
              getObservableFiles({ fileType: DocumentTags.DESTINATION_COUNTRY_STATUS }),
            )
            if ((!v || v === 'NO_STATUS') && !!fileIds.length) {
              fileIds.forEach(id => addPendingDelete(id))
            }
            setHasValidVisa(!!v && v !== 'NO_STATUS')
          }}
        >
          {immigrationStatusList.map(status => (
            <Select.Option key={status.key} label={status.label} value={status.value} />
          ))}
        </Field>
      </Flex.Item>
      {hasValidVisa ? (
        <Flex gap={4} direction="column" basis="100%">
          <Field
            as={FileUploadField}
            allowedFileTypes={ALLOWED_FILE_TYPES}
            application={props.application}
            disabled={props.disabled || !hasValidVisa || props.isRequestingPassportApi}
            fileType={DocumentTags.DESTINATION_COUNTRY_STATUS}
            label={
              !props.disabled ? (
                <Text variant={'bodyM'}>
                  Add the student's{' '}
                  {immigrationStatusList.find(status => status.value === props.immigrationStatus)?.label}{' '}
                  document(s) below, supported file formats: {ALLOWED_FILE_TYPES.map(v => v.toLocaleUpperCase().replace('.', '')).join(', ')} <Asterisk />
                </Text>
              ) : null
            }
            helpText={`Supported file formats: JPG, JPEG, PDF, PNG`}
            name={`immigrationFiles`}
            onRemove={(id: string) =>
              props.setFieldValues({
                immigrationFiles: props.immigrationFiles.filter(file => file.id !== id),
              })
            }
            section={`destinationCountryStatus`}
            validate={(value: string) => {
              const observableFiles = getObservableFiles({
                fileType: DocumentTags.DESTINATION_COUNTRY_STATUS,
                sectionReference: 'destinationCountryStatus',
              })

              if (
                hasValidVisa &&
                !(Object.keys(observableFiles).length || value.length)
              ) {
                return 'This field is required'
              }
              return true
            }}
            showHistory={false}
          />
        </Flex>
      ) : null}
    </Flex>
  )
}